// pos_local /v1 üçün aşağı-səviyyə HTTP client.
// Bütün cavablar { ok, data?, error? } formatındadır.
// Mutation (POST/PATCH/PUT/DELETE) üçün avtomatik Idempotency-Key əlavə olunur.
//
// POS bearer token cloud token-dən AYRIDIR — localStorage 'pos_token'-də saxlanır
// (POS login PIN ilə olur, cloud login email/password ilə). Bax: routes.ts auth.login.

import { POS_HTTP } from './config';

const POS_TOKEN_KEY = 'pos_token';

export interface PosResponse<T = unknown> {
  ok: boolean;
  data?: T;
  error?: { code: string; message: string };
}

export function getPosToken(): string | null {
  try {
    return localStorage.getItem(POS_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setPosToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(POS_TOKEN_KEY, token);
    else localStorage.removeItem(POS_TOKEN_KEY);
  } catch {
    /* noop */
  }
}

// POS sessiyası etibarsız olanda (pos_local 401) çağırılan handler. install.ts
// qeydiyyatdan keçirir → istifadəçini PIN login-ə yönləndirir. Burada saxlanır ki,
// aşağı-səviyyə client UI-dan asılı olmasın (layihə qatlaşması).
let onUnauthorized: (() => void) | null = null;

/** install.ts pos 401 handler-ini qeyd edir (re-auth axını). */
export function setPosUnauthorizedHandler(fn: (() => void) | null): void {
  onUnauthorized = fn;
}

/** Hər mutation üçün unikal idempotency açarı. */
export function genIdempotencyKey(): string {
  const rand =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2);
  return `${Date.now()}-${rand}`;
}

const MUTATION_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export interface PosRequestOptions {
  method: string;
  path: string; // '/v1/...' ilə başlayır
  body?: unknown;
  query?: Record<string, unknown>;
  /** Override idempotency key (default: avtomatik generasiya). */
  idempotencyKey?: string;
  /** Auth bearer (default: pos_token). null versən header göndərilmir. */
  token?: string | null;
}

export async function posRequest<T = unknown>(
  opts: PosRequestOptions,
): Promise<PosResponse<T>> {
  const { method, path, body, query } = opts;

  let url = `${POS_HTTP}${path}`;
  if (query && Object.keys(query).length > 0) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    }
    const s = qs.toString();
    if (s) url += `?${s}`;
  }

  const headers: Record<string, string> = {};

  // Content-Type yalnız body olanda göndərilir. Boş body + application/json
  // fastify-də FST_ERR_CTP_EMPTY_JSON_BODY (400) yaradır → bodysuz POST/DELETE
  // (məs. printer test/delete) sınardı.
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  const token = opts.token === undefined ? getPosToken() : opts.token;
  if (token) headers['Authorization'] = `Bearer ${token}`;

  if (MUTATION_METHODS.has(method.toUpperCase())) {
    headers['Idempotency-Key'] = opts.idempotencyKey || genIdempotencyKey();
  }

  const res = await fetch(url, {
    method,
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  let json: PosResponse<T>;
  try {
    json = (await res.json()) as PosResponse<T>;
  } catch {
    json = {
      ok: false,
      error: { code: 'BAD_RESPONSE', message: `HTTP ${res.status}` },
    };
  }

  // POS sessiyası bitib/ləğv olunub (401) — auth route-ları istisna. pos_token-i
  // təmizlə və re-auth handler-ini işə sal (PIN login). Beləliklə "Login required"
  // səssizcə düşmür, istifadəçi yenidən daxil olur.
  if (res.status === 401 && !path.startsWith('/v1/auth/')) {
    setPosToken(null);
    if (onUnauthorized) {
      try {
        onUnauthorized();
      } catch {
        /* handler xətası sorğu cavabını blok etməsin */
      }
    }
  }

  // HTTP status-u envelope-a daşımaq üçün (adapter status üçün istifadə edir)
  (json as PosResponse<T> & { _status?: number })._status = res.status;
  return json;
}
